// Month keys for BE העסק, shared by the board and its editors.
//
// Extracted the moment the charge editor grew its own month picker: the board
// and the dialog now both name months to the user, and two copies of "how a
// month is spelled" is how a screen ends up saying "ספטמבר 2026" in one place
// and "2026-09" in another.
//
// The key format is the server's: 'YYYY-MM', where lexical order IS
// chronological order (server/lib/crm.js, crm_charges.month).

import { formatDateWithWeekday } from '../../utils/dateFormat';

/** 'YYYY-MM' for a Date. */
export function monthKeyOf(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** 'YYYY-MM' → "ספטמבר 2026". The <input type="month"> shows the raw key;
 *  every other surface reads better as words. */
export function monthLabelOf(month) {
  if (!month) return '';
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('he-IL', { month: 'long', year: 'numeric' });
}

/** 'YYYY-MM' → the ISO day of its last date, for widening a range to cover it.
 *  Day 0 of the NEXT month is the last day of this one, which is what avoids a
 *  28/29/30/31 table and gets February right in a leap year for free. */
export function lastDayOfMonth(month) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m, 0);
  return `${month}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 'YYYY-MM' → "ספטמבר". The month alone, for a label that already says which
 *  month it means by context ("חודש ספטמבר") and would only be lengthened by a
 *  year the reader is standing in. */
export function monthNameOf(month) {
  if (!month) return '';
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('he-IL', { month: 'long' });
}

/** Short axis label — "ספט׳", plus the year only where it changes, so twelve
 *  columns don't repeat "2026" twelve times. */
export function axisLabelOf(month, prevMonth) {
  const [y, m] = month.split('-').map(Number);
  const short = new Date(y, m - 1, 1).toLocaleDateString('he-IL', { month: 'short' });
  const yearChanged = !prevMonth || prevMonth.slice(0, 4) !== month.slice(0, 4);
  return yearChanged ? `${short} ${String(y).slice(2)}` : short;
}

/** 'YYYY-MM-DD' → "יום ב׳ 14/09/26". The weekday is the point of a daily
 *  upload chart — a dip that is a Saturday is not a dip. Built on the shared
 *  formatter so the date reads DD/MM/YY like everywhere else (design-system
 *  C20; full dates never show a four-digit year). */
export function dayLabelOf(day) {
  return formatDateWithWeekday(day);
}

/**
 * The x-axis labels for a run of consecutive days, one entry per day ('' for
 * an unlabelled column).
 *
 * Four tiers by how much room each column has, coarsest last:
 *   • few enough to name every day — the day number;
 *   • up to about a quarter — the month at its 1st, and the day number weekly
 *     (8, 15, 22 — never 29, which sits 1–3 columns before the next month's
 *     label and collides with it once a column is a few pixels wide);
 *   • up to `monthLimit` months — the month at each 1st;
 *   • beyond — the month only where a year opens, same as the month charts.
 */
export function dayAxisLabels(days, { everyDay, monthLimit }) {
  const monthCount = new Set(days.map(d => d.slice(0, 7))).size;
  return days.map((day, i) => {
    const month = day.slice(0, 7);
    const prevMonth = days[i - 1]?.slice(0, 7);
    const dayNum = Number(day.slice(8));
    const opensMonth = i === 0 || month !== prevMonth;
    if (days.length <= everyDay) return String(dayNum);
    if (days.length <= 93) {
      if (opensMonth) return axisLabelOf(month, prevMonth);
      return dayNum % 7 === 1 && dayNum < 29 ? String(dayNum) : '';
    }
    if (monthCount <= monthLimit) return opensMonth ? axisLabelOf(month, prevMonth) : '';
    const opensYear = i === 0 || day.slice(0, 4) !== days[i - 1].slice(0, 4);
    return opensYear ? axisLabelOf(month, prevMonth) : '';
  });
}

/** 'YYYY-MM'..'YYYY-MM' → "אוגוסט 2026" or "מאי 2026 – אוגוסט 2026". Named
 *  spans, not "the previous period": a reader comparing a figure against
 *  something deserves to know what. */
export function spanLabelOf(from, to) {
  if (!from) return '';
  return from === to ? monthLabelOf(from) : `${monthLabelOf(from)} – ${monthLabelOf(to)}`;
}

/** How BE העסק names a period before PEAK went live, everywhere it has to. */
export const PRELAUNCH_NOTE = 'PEAK טרם עלתה לאוויר';

/** Is this 'YYYY-MM' (or 'YYYY-MM-DD') before the go-live month? A plain string
 *  comparison — both formats sort chronologically against 'YYYY-MM'. */
export const isPrelaunch = (liveFrom, month) => !!liveFrom && month < liveFrom;
